export interface CronJobDef {
  name: string;
  functionName: string;
  category: 'polling' | 'forecast' | 'learning' | 'battery' | 'billing' | 'energy' | 'curtailment' | 'automations' | 'telemetry' | 'maintenance' | 'monitoring' | 'reports' | 'analytics' | 'warranty';
  intervalMinutes?: number;
  dailyAtUtc?: string;
  weeklyDay?: number;
  description: string;
}

export const CRON_REGISTRY: CronJobDef[] = [
  // === High-frequency (every 5 min) ===
  {
    name: 'Poll Orchestrator',
    functionName: 'poll-orchestrator',
    category: 'polling',
    intervalMinutes: 5,
    description: 'Polls all vendor APIs for telemetry data',
  },
  {
    name: 'Materialize Orchestrator',
    functionName: 'materialize-orchestrator',
    category: 'telemetry',
    intervalMinutes: 5,
    description: 'Materializes plant-level telemetry from raw data',
  },
  {
    name: 'Evaluate Automations Orchestrator',
    functionName: 'evaluate-automations-orchestrator',
    category: 'automations',
    intervalMinutes: 5,
    description: 'Evaluates automation rules across all orgs',
  },
  {
    name: 'Onboarding Import Worker',
    functionName: 'onboarding-import-worker',
    category: 'telemetry',
    intervalMinutes: 5,
    description: 'Processes queued historical data imports',
  },

  // === Hourly ===
  {
    name: 'Forecast Orchestrator',
    functionName: 'forecast-orchestrator',
    category: 'forecast',
    intervalMinutes: 60,
    description: 'Generates overnight forecasts for all properties',
  },
  {
    name: 'Telemetry Backfill Recent',
    functionName: 'telemetry-backfill-recent',
    category: 'telemetry',
    intervalMinutes: 60,
    description: 'Backfills any gaps in recent telemetry',
  },
  {
    name: 'Rollup Telemetry',
    functionName: 'rollup-telemetry',
    category: 'telemetry',
    intervalMinutes: 60,
    description: 'Rolls up telemetry into hourly/daily aggregates',
  },

  // === Daily ===
  {
    name: 'Billing Cron',
    functionName: 'billing-cron',
    category: 'billing',
    dailyAtUtc: '06:00',
    description: 'Applies volume discounts and billing sync',
  },
  {
    name: 'Reconcile Orchestrator',
    functionName: 'reconcile-orchestrator',
    category: 'energy',
    dailyAtUtc: '06:30',
    description: 'Reconciles daily energy summaries (4 staggered runs/day)',
  },
  {
    name: 'Compute Battery Health Orchestrator',
    functionName: 'compute-battery-health-orchestrator',
    category: 'battery',
    dailyAtUtc: '07:00',
    description: 'Daily battery health snapshots',
  },
  {
    name: 'Build Load Baselines Orchestrator',
    functionName: 'build-load-baselines-orchestrator',
    category: 'learning',
    dailyAtUtc: '08:00',
    description: 'Rebuilds load consumption baselines',
  },
  {
    name: 'Build Daily Trajectories Orchestrator',
    functionName: 'build-daily-trajectories-orchestrator',
    category: 'learning',
    dailyAtUtc: '09:00',
    description: 'Builds daily trajectory fingerprints',
  },
  {
    name: 'Curtailment Orchestrator',
    functionName: 'curtailment-orchestrator',
    category: 'curtailment',
    dailyAtUtc: '08:00',
    description: 'Detects and aggregates curtailment events',
  },
  {
    name: 'Evaluate Forecast Misses Orchestrator',
    functionName: 'evaluate-forecast-misses-orchestrator',
    category: 'forecast',
    dailyAtUtc: '13:00',
    description: 'Evaluates forecast accuracy vs actuals',
  },
  {
    name: 'Compute Forecast Residuals Orchestrator',
    functionName: 'compute-forecast-residuals-orchestrator',
    category: 'forecast',
    dailyAtUtc: '08:00',
    description: 'Computes forecast residuals for accuracy tracking',
  },
  {
    name: 'Billing Invoice Reminders',
    functionName: 'billing-invoice-reminders',
    category: 'billing',
    dailyAtUtc: '14:00',
    description: 'Sends invoice reminder emails',
  },
  {
    name: 'Archive Telemetry',
    functionName: 'archive-telemetry',
    category: 'telemetry',
    dailyAtUtc: '10:00',
    description: 'Archives old telemetry data',
  },

  // === Every 6 hours ===
  {
    name: 'Detect Config Drift',
    functionName: 'detect-config-drift',
    category: 'monitoring',
    intervalMinutes: 360,
    description: 'Compares current settings to baseline; surfaces drift',
  },

  // === Daily (continued) ===
  {
    name: 'Send Monthly Reports',
    functionName: 'send-monthly-reports',
    category: 'reports',
    dailyAtUtc: '12:00',
    description: 'Generates and sends monthly customer summaries',
  },
  {
    name: 'Evaluate Warranty Expirations',
    functionName: 'evaluate-warranty-expirations',
    category: 'warranty',
    dailyAtUtc: '13:00',
    description: 'Fires 90/60/30 day warranty expiration alerts',
  },

  // === Hourly (continued) ===
  {
    name: 'Provider Health Check',
    functionName: 'provider-health-check',
    category: 'monitoring',
    intervalMinutes: 15,
    description: 'Detects vendor API outages and credential failures',
  },

  // === Weekly ===
  {
    name: 'Recompute PV Learning Orchestrator',
    functionName: 'recompute-pv-learning-orchestrator',
    category: 'learning',
    weeklyDay: 0,
    dailyAtUtc: '09:00',
    description: 'Rebuilds PV production baselines from historical data',
  },
  {
    name: 'Evaluate Recommendations',
    functionName: 'evaluate-recommendations',
    category: 'analytics',
    weeklyDay: 1,
    dailyAtUtc: '12:00',
    description: 'Computes property upgrade recommendations',
  },

  // === Monthly ===
  {
    name: 'Apply Installer Credit',
    functionName: 'apply-installer-credit',
    category: 'billing',
    intervalMinutes: 43200,
    description: 'Applies monthly installer credits',
  },

  // === Reconciled with live QStash 2026-07-01 (were scheduled but missing here) ===
  {
    name: 'Process Dunning',
    functionName: 'process-dunning',
    category: 'billing',
    dailyAtUtc: '13:00',
    description: 'Homeowner/operator dunning lifecycle transitions (grace → suspended → dormant)',
  },
  {
    name: 'Reconcile EG4 Devices',
    functionName: 'reconcile-eg4-devices',
    category: 'monitoring',
    dailyAtUtc: '13:00',
    description: 'Detects EG4 device-inventory drift vs the vendor account (GridBOSS/typo/missing serials)',
  },
  {
    name: 'Send Setup Nudge',
    functionName: 'send-setup-nudge',
    category: 'reports',
    dailyAtUtc: '15:00',
    description: 'Onboarding setup-nudge emails for incomplete installs',
  },
  {
    name: 'Compute Data Quality',
    functionName: 'compute-data-quality',
    category: 'monitoring',
    intervalMinutes: 360,
    description: 'Telemetry data-quality scoring across the fleet',
  },
  {
    name: 'Enrich Device Metadata',
    functionName: 'enrich-device-metadata',
    category: 'maintenance',
    dailyAtUtc: '02:00',
    description: 'Refreshes device metadata (model, capacity) from vendor APIs',
  },
  {
    name: 'Fetch Vendor Energy Totals',
    functionName: 'fetch-vendor-energy-totals',
    category: 'energy',
    dailyAtUtc: '03:00',
    description: 'Pulls vendor-reported daily energy totals',
  },
  {
    name: 'Fetch Vendor Events',
    functionName: 'fetch-vendor-events',
    category: 'monitoring',
    dailyAtUtc: '04:00',
    description: 'Pulls vendor alarms/events for the fleet',
  },
  {
    name: 'Build Energy Intervals Orchestrator',
    functionName: 'build-energy-intervals-orchestrator',
    category: 'energy',
    dailyAtUtc: '03:30',
    description: 'Builds 5-minute energy_intervals (PV decontamination pipeline)',
  },
  {
    name: 'Calibrate System Properties',
    functionName: 'calibrate-system-properties',
    category: 'maintenance',
    intervalMinutes: 43200,
    description: 'Monthly recalibration of system capacity/properties',
  },
];

export interface CronJobLateStatus {
  late: boolean;
  overdueMinutes: number;
}

export function isJobLate(job: CronJobDef, lastRunAt: Date | null): CronJobLateStatus {
  if (!lastRunAt) return { late: true, overdueMinutes: Infinity };

  const now = new Date();
  const minutesSinceRun = (now.getTime() - lastRunAt.getTime()) / 60000;

  if (job.intervalMinutes) {
    const threshold = job.intervalMinutes * 2;
    return {
      late: minutesSinceRun > threshold,
      overdueMinutes: Math.max(0, minutesSinceRun - job.intervalMinutes),
    };
  }

  // Daily/weekly jobs: late if >26 hours since last run
  return {
    late: minutesSinceRun > 26 * 60,
    overdueMinutes: Math.max(0, minutesSinceRun - 24 * 60),
  };
}

export const CATEGORY_COLORS: Record<string, string> = {
  polling: 'bg-blue-500/20 text-blue-400',
  forecast: 'bg-purple-500/20 text-purple-400',
  learning: 'bg-cyan-500/20 text-cyan-400',
  battery: 'bg-wt-green-primary/20 text-wt-green-primary',
  billing: 'bg-wt-solar-gold/20 text-wt-solar-gold',
  energy: 'bg-orange-500/20 text-orange-400',
  curtailment: 'bg-wt-amber-primary/20 text-wt-amber-primary',
  automations: 'bg-pink-500/20 text-pink-400',
  telemetry: 'bg-indigo-500/20 text-indigo-400',
  maintenance: 'bg-gray-500/20 text-gray-400',
  monitoring: 'bg-red-500/20 text-red-400',
  reports: 'bg-emerald-500/20 text-emerald-400',
  analytics: 'bg-violet-500/20 text-violet-400',
  warranty: 'bg-yellow-500/20 text-yellow-400',
};
